import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "api/baseurl";
import { toast } from "react-toastify";
import {
  Grid,
  Card,
  Box,
  keyframes,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { encryptBody } from "encryptionAndDecryption/bodyEncryption";
import { decryptBody } from "encryptionAndDecryption/bodyDecryption";
import { fetchSecretKey } from "encryptionAndDecryption/secretKeyDecryption";

const SMTPConfigurationDetails = () => {
  const navigate = useNavigate();
  const loginData = JSON.parse(localStorage.getItem("loginData"));
 const token = JSON.parse(localStorage.getItem("loginData"))?.token;
  const userId = loginData?.userid;

  const [loading, setLoading] = useState(false);
  const [smtpDetails, setSmtpDetails] = useState({
    host: "",
    port: "",
    username: "",
    password: "",
    adminlink:userId
  });
  const [fromAddress, setFromAddress] = useState("");
  const [ccRecipient, setCcRecipient] = useState("");
  const [smtpList, setSmtpList] = useState([]); // For table data
  const [ccList, setCcList] = useState([]);

  useEffect(() => {
    fetchSmtpData();
  }, []);

  const fetchSmtpData = async () => {
    try {
      const secretKey = await fetchSecretKey();
      const response = await axios.get(`/api/accounts/smtp-details/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const decryptedData = decryptBody(response.data, secretKey);
setFromAddress(decryptedData?.fromAddress || "");
setSmtpList(decryptedData?.smtpConfigurations || []);
setCcList(decryptedData?.ccRecipients || []);
    } catch (err) {
      console.error("Error fetching SMTP list:", err);
    //   toast.error("Failed to load existing configurations.");
    }
  };
  const handleSMTPSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const secretKey = await fetchSecretKey();
      const encryptedPayload = encryptBody(smtpDetails, secretKey);

      const response = await axios.post("/api/smtpconfiguration/create", encryptedPayload, {
        headers: {
          "Content-Type": "text/plain",
          Authorization: `Bearer ${token}`,
        },
      });

 if (response.status === 200) {
        const decryptedResponse = decryptBody(response.data, secretKey);

        if (!decryptedResponse || !decryptedResponse.id) {
          throw new Error("Decryption failed or invalid response.");
        }      toast.success(decryptedResponse.message || "SMTP Configuration saved successfully!");

     setSmtpDetails({ host: "", port: "", username: "", password: "" });
      fetchSmtpData();
    } else {
        throw new Error("Unexpected server response.");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.message || "Failed to add SMPTConfiguration .");
    } finally {
      setLoading(false);
    }
  };

  const handleFromAddressSubmit = async () => {
    try {
      const secretKey = await fetchSecretKey();
      const encryptedPayload = encryptBody({id:userId, fromAddress: fromAddress }, secretKey);

      const response = await axios.post("/api/accounts/update/fromaddress", encryptedPayload, {
        headers: {
          "Content-Type": "text/plain",
          Authorization: `Bearer ${token}`,
        },
      });

 if (response.status === 200) {
        const decryptedResponse = decryptBody(response.data, secretKey);

        if (!decryptedResponse || !decryptedResponse.id) {
          throw new Error("Decryption failed or invalid response.");
        }  
              toast.success(decryptedResponse.message || "From Address updated!");
        setFromAddress("");
      fetchSmtpData();
    } else {
        throw new Error("Unexpected server response.");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.message || "Failed to Update From Address .");
    } finally {
      setLoading(false);
    }
  };

  const handleCcSubmit = async () => {
    try {
      const secretKey = await fetchSecretKey();
      const encryptedPayload = encryptBody({ adminId: userId, ccRecipient }, secretKey);

      const response = await axios.post("/api/cc-recipients/create", encryptedPayload, {
        headers: {
          "Content-Type": "text/plain",
          Authorization: `Bearer ${token}`,
        },
      });

      const decryptedResponse = decryptBody(response.data, secretKey);
      toast.success(decryptedResponse.message || "CC Recipient saved!");
       setCcRecipient("");
      fetchSmtpData();
    } catch (err) {
      console.error("CC Error:", err);
      toast.error("Failed to save CC Recipient");
    }
  };

  const spinAnimation = keyframes`
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  `;

  const dotPositions = [
    { top: "5%", left: "50%" },
    { top: "20%", left: "80%" },
    { top: "50%", left: "95%" },
    { top: "80%", left: "80%" },
    { top: "95%", left: "50%" },
    { top: "80%", left: "20%" },
    { top: "50%", left: "5%" },
    { top: "20%", left: "20%" },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            zIndex: 9999,
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: 80,
              height: 80,
              animation: `${spinAnimation} 1.5s linear infinite`,
            }}
          >
            {dotPositions.map((pos, index) => (
              <Box
                key={index}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor:  "#2e7faeff",
                  position: "absolute",
                  ...pos,
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      <Card sx={{ padding: 3, maxWidth: "1400px", margin: "auto" }}>
        <Grid container spacing={4}>
          {/* SMTP Configuration */}
          <Grid item xs={12} md={6}>
            <MDTypography variant="h6" mb={2}>
              SMTP Configuration
            </MDTypography>
            <form onSubmit={handleSMTPSubmit}>
              <Grid container spacing={2}>
                {["host", "port", "username", "password"].map((field) => (
                  <Grid item xs={12} key={field}>
                    <MDInput
                      type={field === "password" ? "password" : "text"}
                      label={field.charAt(0).toUpperCase() + field.slice(1)}
                      fullWidth
                      value={smtpDetails[field]}
                      onChange={(e) =>
                        setSmtpDetails({ ...smtpDetails, [field]: e.target.value })
                      }
                      required
                    />
                  </Grid>
                ))}
              </Grid>
              <MDBox mt={2}>
                <MDButton type="submit" variant="contained" sx={{
    background: "#2e7faeff",
    color: "#fff", 
    "&:hover": {
      background:"#2e7faeff", 
    },
  }}>
                  Create Configuration
                </MDButton>
              </MDBox>
            </form>
          </Grid>

          {/* From Address */}
          <Grid item xs={12} md={3}>
            <MDTypography variant="h6" mb={2}>
              From Address
            </MDTypography>
            <MDInput
              label="Enter From Address"
              fullWidth
              value={fromAddress}
              onChange={(e) => setFromAddress(e.target.value)}
            />
            <MDBox mt={2}>
              <MDButton onClick={handleFromAddressSubmit} variant="contained"  sx={{
    background: "#2e7faeff",
    color: "#fff", 
    "&:hover": {
      background:"#2e7faeff", 
    },
  }}>
                Save
              </MDButton>
            </MDBox>
          </Grid>

          {/* CC Recipients */}
          <Grid item xs={12} md={3}>
            <MDTypography variant="h6" mb={2}>
              CC Recipients
            </MDTypography>
            <MDInput
              label="Enter CC Recipient"
              fullWidth
              value={ccRecipient}
              onChange={(e) => setCcRecipient(e.target.value)}
            />
            <MDBox mt={2}>
              <MDButton onClick={handleCcSubmit} variant="contained"  sx={{
    background: "#2e7faeff",
    color: "#fff", 
    "&:hover": {
      background:"#2e7faeff", 
    },
  }} >
                Save
              </MDButton>
            </MDBox>
          </Grid>
        </Grid>
      </Card>

       {/* Table for Existing Configurations */}
      <Card sx={{ padding: 3, maxWidth: "1400px", margin: "40px auto" }}>
        <MDTypography variant="h6" mb={2}>
          Existing Configurations
        </MDTypography>
        <TableContainer component={Paper}>
        <Table>
  <TableHead>
    <TableRow sx={{ backgroundColor:"#2e7faeff", }}>
      <TableCell align="center" sx={{ fontWeight: "bold", color: "white", border: "1px solid #ddd", width: "350px" }}>
        <b>Host</b>
      </TableCell>
      <TableCell align="center" sx={{ fontWeight: "bold", color: "white", border: "1px solid #ddd", width: "350px" }}>
        <b>Port</b>
      </TableCell>
      <TableCell align="center" sx={{ fontWeight: "bold", color: "white", border: "1px solid #ddd", width: "350px" }}>
        <b>Username</b>
      </TableCell>
      <TableCell align="center" sx={{ fontWeight: "bold", color: "white", border: "1px solid #ddd", width: "350px" }}>
        <b>Password</b>
      </TableCell>
      <TableCell align="center" sx={{ fontWeight: "bold", color: "white", border: "1px solid #ddd", width: "350px" }}>
        <b>From Address</b>
      </TableCell>
      <TableCell align="center" sx={{ fontWeight: "bold", color: "white", border: "1px solid #ddd", width: "350px" }}>
        <b>CC Recipients</b>
      </TableCell>
    </TableRow>

  <TableBody>
    {smtpList.length > 0 ? (
      smtpList.map((item, index) => (
        <TableRow key={index}>
          <TableCell align="center" sx={{ border: "1px solid #ddd" }}>{item.host}</TableCell>
          <TableCell align="center" sx={{ border: "1px solid #ddd" }}>{item.port}</TableCell>
          <TableCell align="center" sx={{ border: "1px solid #ddd" }}>{item.username}</TableCell>
          <TableCell align="center" sx={{ border: "1px solid #ddd" }}>{item.password}</TableCell>

          {index === 0 && (
            <>
              <TableCell align="center" sx={{ border: "1px solid #ddd" }} rowSpan={smtpList.length}>
                {fromAddress || "N/A"}
              </TableCell>
              <TableCell align="center" sx={{ border: "1px solid #ddd" }} rowSpan={smtpList.length}>
                {ccList.length > 0 ? ccList.map(rec => rec.ccRecipient).join(", ") : "N/A"}
              </TableCell>
            </>
          )}
        </TableRow>
      ))
    ) : (
      <TableRow>
        <TableCell colSpan={6} align="center">
          No configurations found.
        </TableCell>
      </TableRow>
    )}
  </TableBody>
    </TableHead>

</Table>

        </TableContainer>
      </Card>
    </DashboardLayout>
  );
};

export default SMTPConfigurationDetails;
